import { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useAuth } from './hooks/useAuth'
import { StageProvider } from './contexts/StageContext'

import Homepage from './components/Homepage'
import FeaturesPage from './components/FeaturesPage'
import PricingPage from './components/PricingPage'
import AboutPage from './components/AboutPage'
import ContactPage from './components/ContactPage'
import PrivacyPolicy from './components/PrivacyPolicy'
import TermsOfService from './components/TermsOfService'
import LoginModal from './components/LoginModal'
import BetaAccessModal from './components/BetaAccessModal'
import IdeaFormWizard from './components/IdeaFormWizard'
import IdeaList from './components/IdeaList'
import IdeaDetail from './components/IdeaDetail'
import IdeaChatModal from './components/IdeaChatModal'
import AuriIntakeModal from './components/AuriIntakeModal'
import AdminPanel from './components/AdminPanel'
import apiService from './services/api'

// Check if user is admin based on their email
const isAdminUser = () => {
  const userEmail = localStorage.getItem('userEmail') || ''
  return userEmail.endsWith('@levantar.ai')
}

// Register page component - shows homepage with login modal open in register mode
const RegisterPage = ({ isAuthenticated, login }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [inviteCode, setInviteCode] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')

  useEffect(() => {
    // Extract invite code and email from URL query params
    const params = new URLSearchParams(location.search)
    const code = params.get('code') || ''
    const email = params.get('email') || ''
    setInviteCode(code)
    setInviteEmail(email)
  }, [location.search])

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app')
    }
  }, [isAuthenticated, navigate])

  return (
    <>
      <Homepage
        onLogin={() => {}}
        onShowFeatures={() => navigate('/features')}
        onShowPricing={() => navigate('/pricing')}
        onShowAbout={() => navigate('/about')}
        onShowContact={() => navigate('/contact')}
        onShowPrivacy={() => navigate('/privacy')}
        onShowTerms={() => navigate('/terms')}
        onBack={() => navigate('/')}
      />
      <LoginModal
        isOpen={true}
        onClose={() => navigate('/')}
        onLogin={login}
        initialMode="register"
        initialInviteCode={inviteCode}
        initialEmail={inviteEmail}
        onGetStarted={() => {}}
      />
    </>
  )
}

RegisterPage.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  login: PropTypes.func.isRequired
}

// Wrapper component to handle public pages with navigation
const PublicLayout = ({ children, isAuthenticated, login }) => {
  const navigate = useNavigate()
  const [showLogin, setShowLogin] = useState(false)
  const [showBetaModal, setShowBetaModal] = useState(false)
  const [loginMode, setLoginMode] = useState('login')

  const handleLogin = (mode = 'register') => {
    if (mode === 'login') {
      setLoginMode('login')
      setShowLogin(true)
    } else {
      setShowBetaModal(true)
    }
  }

  // Redirect to app if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app')
    }
  }, [isAuthenticated, navigate])

  return (
    <>
      {children({
        onLogin: handleLogin,
        onShowFeatures: () => navigate('/features'),
        onShowPricing: () => navigate('/pricing'),
        onShowAbout: () => navigate('/about'),
        onShowContact: () => navigate('/contact'),
        onShowPrivacy: () => navigate('/privacy'),
        onShowTerms: () => navigate('/terms'),
        onBack: () => navigate('/')
      })}
      <BetaAccessModal
        isOpen={showBetaModal}
        onClose={() => setShowBetaModal(false)}
        onSignIn={() => {
          setShowBetaModal(false)
          setLoginMode('login')
          setShowLogin(true)
        }}
        onRegister={() => {
          setShowBetaModal(false)
          setLoginMode('register')
          setShowLogin(true)
        }}
      />
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onLogin={login}
        initialMode={loginMode}
        onGetStarted={() => {
          setShowLogin(false)
          setShowBetaModal(true)
        }}
      />
    </>
  )
}

// Authenticated app component
const AuthenticatedApp = ({ logout }) => {
  const navigate = useNavigate()
  const [currentView, setCurrentView] = useState('list')
  const [selectedIdea, setSelectedIdea] = useState(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [isChatModalOpen, setIsChatModalOpen] = useState(false)
  const [isWelcomeModal, setIsWelcomeModal] = useState(false)
  const [isAuriModalOpen, setIsAuriModalOpen] = useState(false)
  const [auriIdeaDescription, setAuriIdeaDescription] = useState('')
  const [auriTeamMembers, setAuriTeamMembers] = useState([])

  // Check if user is admin
  const isAdmin = isAdminUser()

  // Set up authentication failure handler for API service
  useEffect(() => {
    apiService.setAuthenticationFailureHandler(logout)
  }, [logout])

  // Check for first-time user and show welcome modal
  useEffect(() => {
    const checkFirstTimeUser = async () => {
      // DEBUG: Set to true to force welcome modal to appear
      const FORCE_WELCOME = false

      const hasSeenWelcome = localStorage.getItem('opptora_welcome_shown')
      if (hasSeenWelcome && !FORCE_WELCOME) return

      try {
        const response = await apiService.getIdeas()
        const ideas = response.ideas || response
        const count = Array.isArray(ideas) ? ideas.length : 0

        if (count === 0 || FORCE_WELCOME) {
          setIsAuriModalOpen(true)
          if (!FORCE_WELCOME) {
            localStorage.setItem('opptora_welcome_shown', 'true')
          }
        }
      } catch (error) {
        console.error('Failed to check idea count:', error)
        // If API fails for a new user, still show the welcome modal
        // This handles cases where auth isn't quite ready yet
        setIsAuriModalOpen(true)
        localStorage.setItem('opptora_welcome_shown', 'true')
      }
    }

    checkFirstTimeUser()
  }, [])

  const handleViewChange = (view, idea = null) => {
    setCurrentView(view)
    setSelectedIdea(idea)
    // If navigating back to list from detail, trigger refresh to show updated data
    if (view === 'list' && currentView === 'detail') {
      setRefreshTrigger(prev => prev + 1)
    }
    // Scroll to top when navigating to detail view
    if (view === 'detail') {
      // Use setTimeout to ensure the view change completes first
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        // Fallback: also scroll the document element and body
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
      }, 200)
    }
  }

  const handleIdeaSuccess = async (ideaId) => {
    // If we have an idea ID, navigate to its detail view
    if (ideaId) {
      try {
        const idea = await apiService.getIdea(ideaId)
        setSelectedIdea(idea)
        setCurrentView('detail')
        // Scroll to top
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' })
          document.documentElement.scrollTop = 0
          document.body.scrollTop = 0
        }, 200)
        return
      } catch (err) {
        console.error('Failed to fetch new idea:', err)
        // Fall through to list view on error
      }
    }
    // Fallback: Switch to list view and trigger refresh
    setCurrentView('list')
    setRefreshTrigger(prev => prev + 1)
  }

  // DEBUG: Expose function to window for console access
  useEffect(() => {
    window.showWelcomeModal = () => {
      setIsAuriModalOpen(true)
    }
    return () => {
      delete window.showWelcomeModal
    }
  }, [])

  return (
    <StageProvider initialStage={1}>
      <div style={{ display: currentView === 'list' ? 'block' : 'none' }}>
        <IdeaList
          refreshTrigger={refreshTrigger}
          onSelectIdea={(idea) => handleViewChange('detail', idea)}
          onCreateIdea={() => setIsAuriModalOpen(true)}
          onCreateIdeaChat={() => setIsChatModalOpen(true)}
          onLogout={logout}
          onAdminFeedback={isAdmin ? () => handleViewChange('admin') : undefined}
        />
      </div>
      {currentView === 'create' && (
        <div className="min-h-screen bg-gray-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <IdeaFormWizard
              onSuccess={handleIdeaSuccess}
              onCancel={() => handleViewChange('list')}
            />
          </div>
        </div>
      )}
      {currentView === 'detail' && selectedIdea && (
        <IdeaDetail
          idea={selectedIdea}
          onBack={() => handleViewChange('list')}
          onCreateIdea={() => setIsAuriModalOpen(true)}
          onCreateIdeaChat={() => setIsChatModalOpen(true)}
          onLogout={logout}
        />
      )}
      {currentView === 'admin' && (
        <AdminPanel
          onBack={() => handleViewChange('list')}
          onLogout={logout}
        />
      )}
      <IdeaChatModal
        isOpen={isChatModalOpen}
        onClose={() => {
          setIsChatModalOpen(false)
          setIsWelcomeModal(false)
          setAuriIdeaDescription('')
          setAuriTeamMembers([])
        }}
        onIdeaCreated={handleIdeaSuccess}
        isWelcome={isWelcomeModal}
        initialIdeaDescription={auriIdeaDescription}
        initialTeamMembers={auriTeamMembers}
      />
      <AuriIntakeModal
        isOpen={isAuriModalOpen}
        onClose={() => setIsAuriModalOpen(false)}
        onProceed={(description, auriResponse) => {
          setAuriIdeaDescription(description)
          setAuriTeamMembers(auriResponse?.team_members || [])
          setIsAuriModalOpen(false)
          setIsChatModalOpen(true)
        }}
      />
    </StageProvider>
  )
}

// Protected route wrapper
const ProtectedRoute = ({ isAuthenticated, login, children }) => {
  const navigate = useNavigate()
  const [showLogin, setShowLogin] = useState(false)
  const [showBetaModal, setShowBetaModal] = useState(false)
  const [loginMode, setLoginMode] = useState('login')

  useEffect(() => {
    if (!isAuthenticated) {
      // Show login modal when trying to access protected route
      setShowBetaModal(true)
    }
  }, [isAuthenticated])

  if (!isAuthenticated) {
    return (
      <>
        <Homepage
          onLogin={(mode = 'register') => {
            if (mode === 'login') {
              setLoginMode('login')
              setShowLogin(true)
            } else {
              setShowBetaModal(true)
            }
          }}
          onShowFeatures={() => navigate('/features')}
          onShowPricing={() => navigate('/pricing')}
          onShowAbout={() => navigate('/about')}
          onShowContact={() => navigate('/contact')}
          onShowPrivacy={() => navigate('/privacy')}
          onShowTerms={() => navigate('/terms')}
        />
        <BetaAccessModal
          isOpen={showBetaModal}
          onClose={() => {
            setShowBetaModal(false)
            navigate('/')
          }}
          onSignIn={() => {
            setShowBetaModal(false)
            setLoginMode('login')
            setShowLogin(true)
          }}
          onRegister={() => {
            setShowBetaModal(false)
            setLoginMode('register')
            setShowLogin(true)
          }}
        />
        <LoginModal
          isOpen={showLogin}
          onClose={() => {
            setShowLogin(false)
            navigate('/')
          }}
          onLogin={login}
          initialMode={loginMode}
          onGetStarted={() => {
            setShowLogin(false)
            setShowBetaModal(true)
          }}
        />
      </>
    )
  }

  return children
}

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

const App = () => {
  const { isAuthenticated, login, logout } = useAuth()

  // Get base path from Vite config (set via --base flag in CI for preview environments)
  // This ensures React Router routes match the deployed path (e.g., /feat-prg/)
  const basename = import.meta.env.BASE_URL.replace(/\/$/, '') || '/'

  return (
    <BrowserRouter basename={basename}>
      <ScrollToTop />
      <Routes>
        {/* Public routes */}
        <Route
          path="/"
          element={
            <PublicLayout isAuthenticated={isAuthenticated} login={login}>
              {(props) => <Homepage {...props} />}
            </PublicLayout>
          }
        />
        <Route
          path="/features"
          element={
            <PublicLayout isAuthenticated={isAuthenticated} login={login}>
              {(props) => <FeaturesPage {...props} />}
            </PublicLayout>
          }
        />
        <Route
          path="/pricing"
          element={
            <PublicLayout isAuthenticated={isAuthenticated} login={login}>
              {(props) => <PricingPage {...props} />}
            </PublicLayout>
          }
        />
        <Route
          path="/about"
          element={
            <PublicLayout isAuthenticated={isAuthenticated} login={login}>
              {(props) => <AboutPage {...props} />}
            </PublicLayout>
          }
        />
        <Route
          path="/contact"
          element={
            <PublicLayout isAuthenticated={isAuthenticated} login={login}>
              {(props) => <ContactPage {...props} />}
            </PublicLayout>
          }
        />
        <Route
          path="/privacy"
          element={
            <PublicLayout isAuthenticated={isAuthenticated} login={login}>
              {(props) => <PrivacyPolicy {...props} />}
            </PublicLayout>
          }
        />
        <Route
          path="/terms"
          element={
            <PublicLayout isAuthenticated={isAuthenticated} login={login}>
              {(props) => <TermsOfService {...props} />}
            </PublicLayout>
          }
        />
        <Route
          path="/register"
          element={<RegisterPage isAuthenticated={isAuthenticated} login={login} />}
        />

        {/* Protected app route */}
        <Route
          path="/app"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} login={login}>
              <AuthenticatedApp logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* Catch-all redirect to home */}
        <Route path="*" element={
          <PublicLayout isAuthenticated={isAuthenticated} login={login}>
            {(props) => <Homepage {...props} />}
          </PublicLayout>
        } />
      </Routes>
    </BrowserRouter>
  )
}

export default App
